import React, {useState} from 'react';
import AboutSection from '../components/AboutSection.jsx';
import Footer from '../components/Footer.jsx';
import AppGuideModal from '../components/tutorials/AppGuideModal.jsx';
import BadmintonRulesModal from '../components/tutorials/BadmintonRulesModal.jsx';

const Segment = ({label, sub, active, onClick}) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex flex-1 flex-col items-center gap-1 rounded-2xl py-3.5 text-base font-black transition-all ${
      active
        ? 'bg-white text-slate-900 shadow-lg'
        : 'border border-white/15 bg-white/[0.06] text-white/60'
    }`}
  >
    <span>{label}</span>
    <span className={`text-xs font-bold ${
      active ? 'text-slate-500' : 'text-white/35'
    }`}>
      {sub}
    </span>
  </button>
);

const Chip = ({label, active, onClick}) => (
  <button
    type="button"
    onClick={onClick}
    className={`flex-1 rounded-xl py-2.5 text-sm font-black transition-all ${
      active
        ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-500/30'
        : 'border border-white/15 bg-white/[0.06] text-white/50'
    }`}
  >
    {label}
  </button>
);

const Field = ({label, value, onChange, placeholder, accent}) => (
  <div className="flex items-center gap-2">
    <span
      className="w-5 shrink-0 text-center text-xs font-black"
      style={{color: `${accent}cc`}}
    >
      {label}
    </span>
    <input
      className="min-w-0 flex-1 rounded-xl border border-white/15 bg-white/[0.07] px-3 py-2 text-sm font-bold text-white outline-none placeholder-white/30 focus:border-white/40"
      value={value}
      onChange={event => onChange(event.target.value)}
      placeholder={placeholder}
    />
  </div>
);

const TeamFields = ({
  team,
  teamValue,
  setTeam,
  players,
  setPlayer,
  mode,
  accent,
  classes
}) => (
  <div className={`space-y-2.5 rounded-2xl border p-3.5 ${classes}`}>
    <p className="text-xs font-black uppercase tracking-wider" style={{color: accent}}>
      隊伍 {team}
    </p>
    <Field
      label="隊名"
      value={teamValue}
      onChange={setTeam}
      placeholder={`隊伍 ${team}`}
      accent={accent}
    />
    <div className="h-px bg-white/10" />
    <Field
      label="①"
      value={players[0].value}
      onChange={players[0].set}
      placeholder="球員名稱"
      accent={accent}
    />
    {mode === 'doubles' && (
      <Field
        label="②"
        value={players[1].value}
        onChange={players[1].set}
        placeholder="第二位球員"
        accent={accent}
      />
    )}
  </div>
);

const SetupScreen = ({onStart}) => {
  const [mode, setMode] = useState('doubles');
  const [pts, setPts] = useState(21);
  const [bestOf, setBestOf] = useState(3);
  const [teams, setTeams] = useState({A: '隊伍 A', B: '隊伍 B'});
  const [names, setNames] = useState({
    p1: '球員甲',
    p2: '球員乙',
    p3: '球員丙',
    p4: '球員丁'
  });
  const [showRules, setShowRules] = useState(false);
  const [showGuide, setShowGuide] = useState(false);

  const setTeam = (key, value) => {
    setTeams(current => ({...current, [key]: value}));
  };
  const setPlayer = (key, value) => {
    setNames(current => ({...current, [key]: value}));
  };

  const handleStart = () => {
    onStart({
      teamNameA: teams.A || '隊伍 A',
      teamNameB: teams.B || '隊伍 B',
      playerNameA: mode === 'singles'
        ? names.p1
        : `${names.p1} / ${names.p2}`,
      playerNameB: mode === 'singles'
        ? names.p3
        : `${names.p3} / ${names.p4}`,
      pts,
      bestOf,
      mode,
      names
    });
  };

  return (
    <div className="h-[100dvh] w-screen overflow-y-auto overflow-x-hidden bg-[#020617]">
      <div className="fixed inset-0 pointer-events-none">
        <div className="blob blob-a" />
        <div className="blob blob-b" />
        <div className="grid-bg" />
      </div>

      <div className="relative z-10 flex min-h-[calc(100dvh-2rem)] items-center justify-center px-4 py-5">
        <main className="mx-auto w-full max-w-md space-y-3.5">
          <header className="text-center">
            <div className="mb-1.5 text-4xl" aria-hidden="true">🏸</div>
            <h1 className="text-2xl font-black text-white">羽毛球計分板</h1>
            <p className="mt-1 text-xs font-bold uppercase tracking-widest text-white/40">
              Badminton Scorer
            </p>
          </header>

          <div className="grid grid-cols-2 gap-2.5">
            <button
              type="button"
              onClick={() => setShowRules(true)}
              className="rounded-2xl border border-indigo-500/30 bg-indigo-500/15 px-4 py-3 text-sm font-black text-indigo-300"
            >
              📚 羽毛球規則
            </button>
            <button
              type="button"
              onClick={() => setShowGuide(true)}
              className="rounded-2xl border border-emerald-500/30 bg-emerald-500/15 px-4 py-3 text-sm font-black text-emerald-300"
            >
              📖 使用教學
            </button>
          </div>

          <section className="rounded-2xl border border-white/10 bg-white/[0.05] p-3.5">
            <p className="mb-2.5 text-xs font-black uppercase tracking-widest text-white/50">
              比賽模式
            </p>
            <div className="flex gap-2">
              <Segment
                label="單打"
                sub="Singles · 1v1"
                active={mode === 'singles'}
                onClick={() => setMode('singles')}
              />
              <Segment
                label="雙打"
                sub="Doubles · 2v2"
                active={mode === 'doubles'}
                onClick={() => setMode('doubles')}
              />
            </div>
          </section>

          <section className="grid grid-cols-2 gap-2.5">
            <TeamFields
              team="A"
              teamValue={teams.A}
              setTeam={value => setTeam('A', value)}
              players={[
                {value: names.p1, set: value => setPlayer('p1', value)},
                {value: names.p2, set: value => setPlayer('p2', value)}
              ]}
              mode={mode}
              accent="#818cf8"
              classes="border-indigo-500/35 bg-indigo-500/[0.07]"
            />
            <TeamFields
              team="B"
              teamValue={teams.B}
              setTeam={value => setTeam('B', value)}
              players={[
                {value: names.p3, set: value => setPlayer('p3', value)},
                {value: names.p4, set: value => setPlayer('p4', value)}
              ]}
              mode={mode}
              accent="#fb7185"
              classes="border-rose-500/35 bg-rose-500/[0.07]"
            />
          </section>

          <section className="grid grid-cols-2 gap-2.5">
            <div className="rounded-2xl border border-white/10 bg-white/[0.05] p-3.5">
              <p className="mb-2.5 text-xs font-black uppercase tracking-widest text-white/50">
                局點
              </p>
              <div className="grid grid-cols-2 gap-1.5">
                {[11, 15, 21, 30].map(value => (
                  <Chip
                    key={value}
                    label={value}
                    active={pts === value}
                    onClick={() => setPts(value)}
                  />
                ))}
              </div>
            </div>

            <div className="rounded-2xl border border-white/10 bg-white/[0.05] p-3.5">
              <p className="mb-2.5 text-xs font-black uppercase tracking-widest text-white/50">
                賽制
              </p>
              <div className="flex flex-col gap-1.5">
                {[1, 3, 5].map(value => (
                  <Chip
                    key={value}
                    label={`${value} 局制`}
                    active={bestOf === value}
                    onClick={() => setBestOf(value)}
                  />
                ))}
              </div>
            </div>
          </section>

          <button
            type="button"
            onClick={handleStart}
            className="w-full rounded-2xl bg-white py-4 text-lg font-black text-slate-900 shadow-xl hover:bg-indigo-400 hover:text-white"
          >
            開始比賽 🏸
          </button>

          <AboutSection />
        </main>
      </div>

      <Footer centered />
      <BadmintonRulesModal
        open={showRules}
        onClose={() => setShowRules(false)}
      />
      <AppGuideModal
        open={showGuide}
        onClose={() => setShowGuide(false)}
      />
    </div>
  );
};

export default SetupScreen;